import React, { useState, useRef, useEffect } from 'react';

/* ════════ BUILDER STACK ════════
   已从 /redesign 复制版转正（原 SkillSectionRedesign，组件名还原）。
   AI Tooling 卡与紧凑行距为用户验收稿；overflow-x 用 clip 防止隐式二级滚动条。 */

const skills = [
  { name: 'Python', iconUrl: '/images/python.png' },
  { name: 'Linux System', iconUrl: '/images/linux.png' },
  { name: 'Shell Scripting', iconUrl: '/images/shell-scripting.png' },
  { name: 'Docker ', iconUrl: '/images/docker.png' },
  { name: 'Discord Bot Dev', iconUrl: '/images/discord-bot.png' },
  { name: 'Community Management', iconUrl: '/images/community-management.png' },
  { name: 'Crypto Research', iconUrl: '/images/crypto-research.png' },
  { name: 'AI Tooling', iconUrl: '/images/ai.png' },
  { name: 'Regional X Account', iconUrl: '/images/regional-x.png' },
  { name: 'Event Operation', iconUrl: '/images/event-operation.png' },
  { name: 'Doc Writing', iconUrl: '/images/doc-writing.png' },
  { name: 'Event Design', iconUrl: '/images/event-design.png' },
];

const coreStats = [
  '4+ Years in the Blockchain Industry',
  '3+ Years in Discord Management (10h+ Daily Online)',
  'Node running',
  'Full-time Web3 Builder',
];

const SkillCard = ({ skill, index, isVisible }) => {
  return (
    <div className={`skill-card ${isVisible ? 'visible' : ''}`} style={{ animationDelay: isVisible ? `${index * 0.1}s` : '0s' }}>
      {skill.iconUrl && (
        <img src={skill.iconUrl} alt={skill.name} className="w-5 h-5" />
      )}
      <span className="bitcount-grid-single">{skill.name}</span>
    </div>
  );
};

const SkillSection = () => {
  const sectionRef = useRef(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.unobserve(entry.target);
        }
      },
      { threshold: 0.2 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
    };
  }, []);

  return (
    <div className="skill-section" ref={sectionRef}>
      <style>{`
        .skill-section {
          position: relative;
          background-color: #FFFDF5;
          box-shadow:
            0 -40px 80px rgba(0, 0, 0, 0.01),
            0  40px 80px rgba(0, 0, 0, 0.01);
          padding: 120px 0;
          overflow-x: clip; /* clip 纯裁剪不产生滚动容器；hidden 会隐式把 y 变 auto 出二级滚动条 */
        }
        .skill-section::before,
        .skill-section::after {
          content: "";
          position: absolute;
          left: 0;
          right: 0;
          height: 50px;
          pointer-events: none;
        }
        .skill-section::before {
          top: -50px;
          background: linear-gradient(to bottom, #FDFAED 0%, #FFFDF5 100%);
        }
        .skill-section::after {
          bottom: -50px;
          background: linear-gradient(to bottom, #FFFDF5 0%, #FDFAED 100%);
        }
        .builder-left {
          opacity: 0;
          transform: translateX(-50px);
          transition: opacity 0.8s cubic-bezier(0.22, 1, 0.36, 1), transform 0.8s cubic-bezier(0.22, 1, 0.36, 1);
        }
        .builder-left.visible {
          opacity: 1;
          transform: translateX(0);
        }
        .builder-right {
          opacity: 0;
          transform: translateX(50px);
          transition: opacity 0.8s cubic-bezier(0.22, 1, 0.36, 1), transform 0.8s cubic-bezier(0.22, 1, 0.36, 1);
        }
        .builder-right.visible {
          opacity: 1;
          transform: translateX(0);
        }
        .skill-card {
          opacity: 0;
          transform: translateY(20px);
          animation: cardFadeIn 0.5s ease forwards;
        }
        .skill-card.visible {
          opacity: 1;
          transform: translateY(0);
        }
        @keyframes cardFadeIn {
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        .skill-card {
          position: relative;
          display: flex;
          align-items: center;
          gap: 0.75rem;
          padding: 0.875rem 1rem;
          border: 1px solid rgba(0, 0, 0, 0.08);
          border-radius: 0.5rem;
          background: transparent;
          transition: background 0.2s ease;
        }
        .skill-card:hover {
          background: rgba(0, 0, 0, 0.02);
        }
        .builder-stats {
          font-family: 'Bitcount Grid Single', monospace;
          font-size: 1.2rem;
          font-weight: 700;
          color: #4b5563;
          line-height: 1.5;
        }
        .builder-stats > div {
          margin-bottom: 0.9rem;
        }
        .builder-stats > div:last-child {
          margin-bottom: 0;
        }
      `}</style>
      <div className="max-w-6xl mx-auto px-8">
        <div className="flex flex-col md:flex-row gap-8 md:gap-12">
          {/* 左侧 - 个人简介 */}
          <div className={`w-full md:w-[40%] builder-left ${isVisible ? 'visible' : ''}`}>
            <h2 className="goldman-regular text-4xl md:text-5xl font-black text-gray-800 mb-6">
              BUILDER STACK
            </h2>
            <div className="builder-stats">
              {coreStats.map((stat, index) => (
                <div key={index}>{stat}</div>
              ))}
            </div>
          </div>

          {/* 右侧 - 技能矩阵 */}
          <div className={`w-full md:w-[60%] builder-right ${isVisible ? 'visible' : ''}`}>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-6">
              {skills.map((skill, index) => (
                <SkillCard key={index} skill={skill} index={index} isVisible={isVisible} />
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SkillSection;
